import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useFirebase } from '@/firebase';
import type { Novel } from '@/lib/types';

export interface UserNovelsContextState {
  ownedNovels: Novel[];
  isLoading: boolean;
  error: Error | null;
}

export const UserNovelsContext = createContext<UserNovelsContextState | undefined>(undefined);

export function UserNovelsProvider({ children }: { children: React.ReactNode }) {
  const { user, backend } = useFirebase();
  const [ownedNovels, setOwnedNovels] = useState<Novel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!user || !backend) {
      setOwnedNovels([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const unsubscribe = backend.novels.subscribeNovels(
      user.uid,
      (list) => {
        setOwnedNovels(list);
        setIsLoading(false);
      },
      (err) => {
        console.error('Failed to subscribe to owned novels:', err);
        setError(err);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [backend, user]);

  const value = useMemo(() => ({
    ownedNovels,
    isLoading,
    error,
  }), [ownedNovels, isLoading, error]);

  return (
    <UserNovelsContext.Provider value={value}>
      {children}
    </UserNovelsContext.Provider>
  );
}

export function useUserNovels() {
  const context = useContext(UserNovelsContext);
  if (context === undefined) {
    throw new Error('useUserNovels must be used within a UserNovelsProvider');
  }
  return context;
}
